import { useEffect, useId, useLayoutEffect, useRef } from 'react';

// Saved has a compact filter picker; the horizontal list elsewhere in the app
// keeps its existing browsing behavior.
export default function SavedEmojiFilter({ lists, selectedId, selectedName, onChange, open, onOpenChange, disabled }) {
    const rootRef = useRef(null);
    const triggerRef = useRef(null);
    const popupRef = useRef(null);
    const gridRef = useRef(null);
    const popupId = useId();
    const titleId = useId();

    const close = (restoreFocus = false) => {
        onOpenChange(false);
        if (restoreFocus) triggerRef.current?.focus({ preventScroll: true });
    };

    useLayoutEffect(() => {
        if (!open) return;
        const selected = popupRef.current.querySelector('[aria-pressed="true"]');
        selected?.focus({ preventScroll: true });
        // Only move the emoji grid, never the underlying Saved page.
        const grid = gridRef.current;
        if (selected && grid?.contains(selected)) {
            grid.scrollTop = Math.max(0, selected.offsetTop - (grid.clientHeight - selected.offsetHeight) / 2);
        }
    }, [open]);

    useEffect(() => {
        if (!open) return;
        const onPointerDown = event => {
            if (!rootRef.current.contains(event.target)) onOpenChange(false);
        };
        document.addEventListener('pointerdown', onPointerDown, true);
        return () => document.removeEventListener('pointerdown', onPointerDown, true);
    }, [open, onOpenChange]);

    const select = id => {
        close(true);
        if ((id || null) !== (selectedId || null)) onChange(id);
    };
    const onKeyDown = event => {
        if (event.key === 'Escape') {
            event.preventDefault();
            event.stopPropagation();
            close(true);
            return;
        }
        if (!gridRef.current?.contains(event.target)) return;
        const buttons = [...gridRef.current.querySelectorAll('button')];
        const index = buttons.indexOf(event.target);
        // Measure the actual first row: dividing widths loses the grid gaps and
        // scrollbar, and can turn a five-column grid into a six-column jump.
        const columns = Math.max(1, buttons.filter(button => button.offsetTop === buttons[0]?.offsetTop).length);
        const next = {
            ArrowLeft: index - 1,
            ArrowRight: index + 1,
            ArrowUp: index - columns,
            ArrowDown: index + columns,
            Home: 0,
            End: buttons.length - 1,
        }[event.key];
        if (next === undefined) return;
        event.preventDefault();
        const target = buttons[Math.max(0, Math.min(buttons.length - 1, next))];
        if (!target) return;
        target.focus({ preventScroll: true });
        const grid = gridRef.current;
        const top = target.offsetTop;
        if (top < grid.scrollTop) grid.scrollTop = top;
        else if (top + target.offsetHeight > grid.scrollTop + grid.clientHeight) {
            grid.scrollTop = top + target.offsetHeight - grid.clientHeight;
        }
    };

    return <div ref={rootRef} className='savedEmojiFilter' onKeyDown={onKeyDown}
        onBlur={event => {
            if (open && event.relatedTarget && !event.currentTarget.contains(event.relatedTarget)) close();
        }}>
        <button ref={triggerRef} type='button' className={`savedEmojiFilterButton${selectedId ? ' savedEmojiFilterButtonSelected' : ''}`}
            disabled={disabled} aria-label={`Filter by emoji: ${selectedName || 'All saved'}`}
            aria-haspopup='dialog' aria-expanded={open} aria-controls={open ? popupId : undefined}
            onClick={() => onOpenChange(!open)}>
            {selectedName || 'All'}
        </button>
        {open && <div ref={popupRef} id={popupId} className='savedEmojiFilterPopup' role='dialog' aria-labelledby={titleId}>
            <div className='savedEmojiFilterHeading'><h2 id={titleId}>Filter by emoji</h2>
                <button type='button' aria-label='Close emoji filters' onClick={() => close(true)}>
                    <svg viewBox='0 0 24 24' aria-hidden='true'><path d='m7 7 10 10M7 17 17 7' /></svg>
                </button>
            </div>
            <button type='button' className='savedEmojiFilterAll' aria-pressed={!selectedId} onClick={() => select(null)}>
                <span>All saved</span><span aria-hidden='true'>{!selectedId ? '✓' : ''}</span>
            </button>
            {lists.length > 0 ? <div ref={gridRef} className='savedEmojiFilterGrid' role='group' aria-label='Emoji filters'>
                {lists.map(list => <button type='button' key={list.id} aria-label={`Filter by ${list.name}`}
                    aria-pressed={list.id === selectedId} onClick={() => select(list.id)}>
                    <span aria-hidden='true'>{list.name}</span>
                    {list.id === selectedId && <span className='savedEmojiFilterCheck' aria-hidden='true'>✓</span>}
                </button>)}
            </div> : <p className='savedEmojiFilterEmpty'>Your emoji lists will appear here.</p>}
        </div>}
    </div>;
}
